import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  catchError,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { AppExceptionModel } from '../../models/app-exception.model';
import { GroupType } from '../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../models/member.school.model';
import { MembershipStatus } from '../../models/membership.status';
import { UserModel } from '../../models/user.model';
import { SchoolMemberService } from '../../services/member.school.service';
import * as groupActions from '../actions/group.actions';
import * as lessonActions from '../actions/lesson.school.actions';
import { getMembersFailure } from '../actions/member.actions';
import * as memberActions from '../actions/member.school.actions';
import * as sessionActions from '../actions/session.school.actions';
import * as teacherActions from '../actions/teacher.school.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';
import * as groupSelector from '../selectors/group.selector';
import * as teacherSelector from '../selectors/teacher.school.selector';

@Injectable()
export class TeacherEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  memberService: SchoolMemberService = inject(SchoolMemberService);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);
  location: Location = inject(Location);

  user: UserModel | null = null;
  userSub: Subscription | undefined;

  private logout$ = new Subject<void>();

  constructor() {
    this.userSub = this.store
      .select((state) => state.user.user)
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  // Effect to fetch Members
  getStudents$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(teacherActions.getStudents),
      mergeMap(
        ({
          source,
          groupId,
          status,
          gradeId,
          academicYear,
          lastRecord,
          limit,
        }) => {
          // console.log(
          //   `getStudents$ :: Called: ${source}, GroupId: ${groupId}, GradeId: ${gradeId}`
          // );
          return this.memberService.getMembers({
            source: source,
            groupId: groupId,
            getMemberType: [SchoolMemberType.student],
            gradeId: gradeId,
            academicYear: academicYear,
            status: status,
            lastRecord: lastRecord,
            limit: limit ?? 15,
          });
        },
      ),
      mergeMap((members) => {
        if (members.length === 0) return of(teacherActions.getStudentsDone());

        const finalMembers = members.map((member) => {
          return this.afs
            .doc<UserModel>('users/' + member.memberId)
            .get()
            .pipe(
              map((snapshot) => {
                if (!snapshot.exists) return null; // School group does not allow unregistered users
                let memberDoc: SchoolMember = Object.assign(
                  {},
                  snapshot.data(),
                  member,
                );
                return memberDoc;
              }),
            );
        });

        return forkJoin(finalMembers).pipe(
          map((resolvedMembers) => {
            const validMembers = resolvedMembers.filter(
              (member) => member !== null,
            );
            // return of(teacherActions.addMemberToStore({member: memberDoc}));
            return teacherActions.addStudentsToStore({ members: validMembers });
          }),
        );
      }),
      catchError((error) => {
        console.log('getStudents$ :: Error:', error);
        return of(teacherActions.getStudentsFailure({ error }));
      }),
    ),
  );

  // Effect to fetch Members
  getStudent$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(teacherActions.getStudent),
      mergeMap(({ groupId, memberId }) =>
        this.memberService.getMember(groupId, memberId),
      ),
      mergeMap((member) => {
        return member.pipe(
          take(1),
          mergeMap((memberDoc) => {
            if (memberDoc === null) {
              return of(
                getMembersFailure({
                  error: {
                    code: 'ERR_NOT_FOUND',
                    title: 'Student not found',
                    message: 'The student you are looking for does not exist.',
                  } as AppExceptionModel,
                }),
              );
            }
            return this.afs
              .doc<UserModel>('users/' + memberDoc.memberId)
              .get()
              .pipe(
                switchMap((userSnapshot) => {
                  if (!userSnapshot.exists) return EMPTY;
                  let memberResult: SchoolMember = Object.assign(
                    {},
                    memberDoc,
                    userSnapshot.data(),
                  );
                  return of(
                    teacherActions.addStudentToStore({ member: memberResult }),
                  );
                }),
              );
          }),
        );
      }),
      catchError((error) => {
        console.log('getStudent$ :: Error:', error);
        return of(teacherActions.getStudentsFailure({ error }));
      }),
    ),
  );

  // If the logged-in user is a teacher, get their class students
  initTeacherRole$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId }, group]) => {
        if (!group || group.groupType !== GroupType.school) {
          return EMPTY;

          // If user is a teacher with assigned grade, get their students
        } else if (
          !(group.memberType as SchoolMemberType[]).includes(
            SchoolMemberType.teacher,
          ) ||
          !group.gradeId ||
          group.gradeId === 'unassigned'
        ) {
          return EMPTY;
        } else {
          return of(
            teacherActions.getStudents({
              source: 'TeacherEffects :: loadMembersByRole$ : students',
              groupId: group.id!,
              status: [MembershipStatus.active],
              gradeId: group.gradeId,
              limit: 50,
            }),
            lessonActions.getLessons({
              groupId: group.id!,
              gradeId: group.gradeId,
              // academicYear: group.academicYear,
            }),
            sessionActions.getSessions({
              groupId: group.id!,
              academicYear: group.academicYear,
              gradeId: group.gradeId,
            }),
          );
        }
      }),
      catchError((error) => {
        console.log('initTeacherRole$ :: Error:', error);
        return of(teacherActions.getStudentsFailure({ error }));
      }),
    ),
  );

  initParentRole$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId }, group]) => {
        if (!group) return EMPTY;

        // If user is a Parent with tagged students(children), get their records
        if (
          !(group.memberType as SchoolMemberType[]).includes(
            SchoolMemberType.parent,
          ) ||
          !group.studentId
        )
          return EMPTY;

        return of(
          teacherActions.getChildrenByParentId({
            groupId: group.id!,
            memberId: group.memberId,
          }),
        );
      }),
      catchError((error) => {
        console.log('initParentRole$ :: Error:', error);
        return of(teacherActions.getStudentsFailure({ error }));
      }),
    ),
  );

  // Effect to fetch Members
  getChildrenByParentId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(teacherActions.getChildrenByParentId),
      mergeMap(({ groupId, memberId }) =>
        this.memberService.getMembersByParentId(groupId, memberId),
      ),
      mergeMap((members) => {
        if (members.length === 0)
          return of(teacherActions.getChildrenDone({ members: [] }));

        const finalMembers = members.map((member) => {
          return this.afs
            .doc<UserModel>('users/' + member.memberId)
            .get()
            .pipe(
              map((snapshot) => {
                if (!snapshot.exists) return null; // School group does not allow unregistered users
                let memberDoc: SchoolMember = Object.assign(
                  {},
                  snapshot.data(),
                  member,
                );
                return memberDoc;
              }),
            );
        });

        return forkJoin(finalMembers).pipe(
          map((resolvedMembers) => {
            const validMembers = resolvedMembers.filter(
              (member) => member !== null,
            );
            // return of(teacherActions.addMemberToStore({member: memberDoc}));
            return teacherActions.getChildrenDone({ members: validMembers });
          }),
        );
      }),
      catchError((error) => {
        console.log('getChildrenByParentId$ :: Error:', error);
        return of(teacherActions.getStudentsFailure({ error }));
      }),
    ),
  );

  // Effect to update a grade
  syncLocalMemberGradeChange$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.updateSuccess),
      switchMap(({ member }) => {
        return this.store
          .select(teacherSelector.getSchoolMemberById(member.memberId))
          .pipe(
            take(1),
            switchMap((student) => {
              if (student === null || student === undefined) {
                return EMPTY;
              }
              return of(teacherActions.addStudentToStore({ member }));
            }),
          );
      }),
    ),
  );

  clearMembers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(teacherActions.clear());
      }),
    ),
  );
}
